import { AnalyticsEvent, AnalyticsService, ABTestService, ConsentPreferences } from '@/types/analytics';
import { PerformanceAnalytics } from './analytics/performanceAnalytics';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

// Supabase project constants (same values as in client.ts)
const SUPABASE_URL = "https://wvgoatayeagzvkpzmunv.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind2Z29hdGF5ZWFnenZrcHptdW52Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM5Njg5NjgsImV4cCI6MjA1OTU0NDk2OH0.8vPQhByS7bo0iomsRX14PAvRDYvGH59tuNVqpIOT6K4";

// Simple implementation of ABTestService
const abTestingService: ABTestService = {
  registerTest: (config) => {
    logger.debug('Analytics: Register test', config);
  },
  assignVariant: (testId) => {
    logger.debug('Analytics: Assign variant for test', testId);
    return 'control';
  },
  getVariant: (testId) => {
    logger.debug('Analytics: Get variant for test', testId);
    return null;
  },
  trackExposure: (testId, variant) => {
    logger.debug('Analytics: Track exposure', { testId, variant });
  },
  trackConversion: (testId, variant) => {
    logger.debug('Analytics: Track conversion', { testId, variant });
  }
};

// Helper to detect traffic source
function detectTrafficSource(): string {
  const referrer = document.referrer;
  if (!referrer) return 'Direct';
  
  // Detect search engines
  if (referrer.match(/google\.|bing\.|yahoo\.|duckduckgo\.|yandex\./i)) 
    return 'Organic Search';
  
  // Detect social media
  if (referrer.match(/facebook\.|instagram\.|twitter\.|tiktok\.|pinterest\.|linkedin\./i))
    return 'Social Media';
  
  // Detect email clients (this is a simplification)
  if (referrer.match(/mail\.|outlook\.|gmail\./i))
    return 'Email';
  
  return 'Referral';
}

// Helper to detect device type
function detectDeviceType(): string {
  const userAgent = navigator.userAgent || '';
  
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent))
    return 'Mobile';
  
  if (/Tablet|iPad/i.test(userAgent))
    return 'Tablet';
  
  return 'Desktop';
}

// Helper to get browser information
function getBrowserInfo(): string {
  const userAgent = navigator.userAgent || '';
  
  if (userAgent.indexOf("Firefox") > -1) return "Firefox";
  if (userAgent.indexOf("SamsungBrowser") > -1) return "Samsung";
  if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) return "Opera";
  if (userAgent.indexOf("Trident") > -1) return "Internet Explorer";
  if (userAgent.indexOf("Edge") > -1) return "Edge";
  if (userAgent.indexOf("Chrome") > -1) return "Chrome";
  if (userAgent.indexOf("Safari") > -1) return "Safari";
  
  return "Unknown";
}

// Helper to get OS information
function getOperatingSystem(): string {
  const userAgent = navigator.userAgent || '';
  
  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "MacOS";
  if (/Linux/i.test(userAgent)) return "Linux";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  
  return "Unknown";
}

// Create or get a session ID
function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : 
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

// Initialize performance analytics
const performanceAnalytics = new PerformanceAnalytics();

// Create a simple placeholder implementation
export const analytics: AnalyticsService = {
  setUserId: (userId) => {
    logger.debug('Analytics: Set user ID', userId);
  },
  
  trackPageView: (path, title) => {
    logger.performance('Analytics: Page view', { path, title });
    analytics.trackVisit(path);
  },
  
  trackVisit: async (page) => {
    try {
      const sessionId = getOrCreateSessionId();
      const currentSession = await supabase.auth.getSession();
      const token = currentSession.data?.session?.access_token;

      const visitData = {
        page,
        source: detectTrafficSource(),
        device_type: detectDeviceType(),
        session_id: sessionId,
        referrer: document.referrer || null,
        browser: getBrowserInfo(),
        os: getOperatingSystem(),
      };

      logger.debug('Recording visit data via edge function:', visitData);

      // POST to edge function so Cloudflare's CF-IPCountry header is captured server-side
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/track-visit`, {
        method: 'POST',
        headers,
        body: JSON.stringify(visitData),
      });

      if (!response.ok) {
        console.warn('[Analytics] Failed to record visit via edge function:', response.status);
      } else {
        logger.debug('Successfully recorded visit');
      }
    } catch (error) {
      console.warn('[Analytics] Error tracking visit:', error);
    }
  },
  
  trackEvent: (event: AnalyticsEvent) => {
    logger.debug('Analytics: Event', event);
  },
  
  setConsentPreferences: (preferences) => {
    logger.debug('Analytics: Set consent preferences', preferences);
  },
  
  getABTesting: () => {
    return abTestingService;
  },
  
  initPerformanceTracking: () => {
    performanceAnalytics.initializePerformanceTracking();
  },
  
  getPerformanceMetrics: () => {
    return performanceAnalytics.getCurrentMetrics();
  }
};

// Automatically initialize performance tracking
if (typeof window !== 'undefined') {
  // Wait for page to load to avoid competing for resources during initial render
  window.addEventListener('load', () => {
    setTimeout(() => {
      analytics.initPerformanceTracking();
    }, 1000); // Delay by 1s after load to prioritize user interaction
  });
}
